/*

Dependency Prepend Design:

[boughtPlans, cartPlans, depN...dep2'sDep3, dep1'sDep2, targetPlan'sDep1, targetPlan];

* for NetNew / Free2Paid, boughtPlans is empty
* cartPlans, can be empty or preset plans
* init stage: make sure that [boughtPlans + cartPlans] are validated in this way
* when user adds a target plan:
* if target plan conflicts.in(boughtPlans + cartPlans), stop & alert
* else if target plan requires none, add to cart
* else if target plan oneof.requires.included.in(boughtPlans + cartPlans), add target plan to cart
* else if target plan noneof.requires.included.in(boughtPlans + cartPlans), prepend dep1 before target plan
* index goes to end of [boughtPlans, cartPlans]
* check dep1's dep2 with the above logic, if yes, prepend dep2 before dep1
* index goes to end of [boughtPlans, cartPlans]
* ...
* until index reaches the end of the list, add the whole list (depN...dep1, targetPlan) to the cart
*/

/**
 * Builder class for creating requires and conflict rules with chainable API
 */
class RequiresBuilder {
  constructor(currentCategories) {
    this.currentCategories = Array.isArray(currentCategories) ? currentCategories : [currentCategories];
  }

  /**
   * Set required categories
   * @param {string|string[]} requiredCategories - Categories that are required
   * @returns {Object} Returns object with current and required properties
   */
  require(requiredCategories, options) {
    const required = Array.isArray(requiredCategories) ? requiredCategories : [requiredCategories];
    return {
      current: this.currentCategories,
      required: required,
      billingCycleMatch: options?.billingCycleMatch ?? true,
      billingCycleEqual: options?.billingCycleEqual ?? false,
      licenseMatch: options?.licenseMatch ?? true,
      licenseEqual: options?.licenseEqual ?? false,
    };
  }

  /**
   * Set conflicting categories
   * @param {string|string[]} conflictCategories - Categories that conflict with current
   * @param {Object} options - Options for the exclusion rule (e.g., { sameCategoryReplacement: false, cascadeRemoval: true })
   * @returns {Array<Array<string>|Object>} Returns array of [current, conflict] pairs or objects with categories and options
   * @example
   * // Single current category with multiple conflicts (default options)
   * this.current('aaa').conflict(['bbb', 'ccc'])
   * // Returns: [['aaa', 'bbb'], ['aaa', 'ccc']]
   *
   * // Multiple current categories with conflicts (default options)
   * this.current(['aaa', 'ddd']).conflict(['bbb', 'ccc'])
   * // Returns: [['aaa', 'bbb'], ['aaa', 'ccc'], ['ddd', 'bbb'], ['ddd', 'ccc']]
   *
   * // With custom options
   * this.current('aaa').conflict(['bbb'], { sameCategoryReplacement: false })
   * // Returns: [{ categories: ['aaa', 'bbb'], options: { sameCategoryReplacement: false } }]
   */
  conflict(conflictCategories, options) {
    const conflicts = Array.isArray(conflictCategories) ? conflictCategories : [conflictCategories];
    const result = [];

    // Generate all pairs: each current category conflicts with each conflict category
    for (const current of this.currentCategories) {
      for (const conflict of conflicts) {
        // If options are provided, return object format; otherwise return array format for backward compatibility
        if (options && Object.keys(options).length > 0) {
          result.push({
            categories: [current, conflict],
            options: options,
          });
        } else {
          result.push([current, conflict]);
        }
      }
    }

    return result;
  }
}

export default class RuleInterface {
  /**
   * Create a builder for defining requires rules with chainable API
   * @param {string|string[]} currentCategories - Current plan categories
   * @returns {RequiresBuilder} Builder instance for chaining
   * @example
   * // Single category
   * return this.current('cmr30').require(['pro', 'biz']);
   *
   * // Multiple categories
   * return this.current(['cmr30', 'cmr40']).require(['pro', 'biz']);
   *
   * // Using imported categories
   * return this.current(CMRCategories).require(basePlanCategories);
   */
  current(currentCategories) {
    return new RequiresBuilder(currentCategories);
  }

  async requires() {} // require the other plans
  async conflicts() {} // make sure it is not conflict with the other plans

  async beforeAdd() {} // before add to the cart
  async added() {} // after added to the cart

  async beforeUpdate() {} // before update the cart
  async updated() {} // cart is updated, should revalidate

  async beforeRemove() {} // before remove from the cart
  async removed() {} // after removed from the cart
}
